/**
 * Where the pages break, drawn on the paper.
 *
 * A DOCUMENT IS A PAGE, AND UNTIL NOW IT WAS ONE VERY LONG PAGE. Writing a procedure means knowing
 * what lands on page three, whether a table fits, and whether a heading is about to be stranded at
 * the foot of a sheet with its section overleaf. A box that simply grows answers none of that, and
 * the author only finds out at the printer.
 *
 * THIS IS A PREVIEW, NOT A LAYOUT ENGINE, and the distinction is the whole design. Nothing is
 * stored, nothing enters the document, and the rendition that produces the signed PDF remains the
 * authority on where a page ends. What this does is measure the blocks the browser has ALREADY
 * laid out and push the ones that straddle a sheet boundary onto the next sheet -- so the editor
 * shows the breaks the printer will, from the same measurements.
 *
 * DECORATIONS, NOT INLINE STYLES, and that is the whole lesson of the first attempt. Writing
 * `block.style.paddingTop` worked exactly once per keystroke and then vanished: ProseMirror watches
 * the editable DOM and reconciles anything it did not put there back out of existence. The pushes
 * WERE applied -- nine of them, measurably -- and were gone by the time anything looked. A
 * decoration is ProseMirror's own mechanism for "draw this differently without changing the
 * document": it survives reconciliation, carries no text, and is invisible to the CRDT, the save
 * and every other editor on the document.
 *
 * MEASURED ONCE, THEN COMPUTED. The browser is asked for each block's position and height a single
 * time, and the new breaks are worked out in arithmetic from there -- so a document that needs
 * nine pushes costs one layout pass rather than nine. The measurement subtracts the pushes already
 * in place, which is what makes it stable: given a settled page it computes the same answer and
 * dispatches nothing.
 *
 * A BLOCK TALLER THAN A SHEET IS LEFT WHERE IT IS. Moving it would only move the problem, and
 * splitting it is the renderer's job, not a preview's.
 */
import { Extension } from "@tiptap/core";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";
import { PAGE_GAP as GAP, pageGeometry, pageSetup } from "./PageElements";
import { Band, bandsFor, grown } from "./footnotes";

/** US Letter at the 96 CSS pixels a browser resolves an inch to. */
// THE DEFAULTS, AND ONLY THE DEFAULTS. These are letter portrait with an inch top and bottom, kept
// because a document that has never had its page setup touched must lay out exactly as it did
// before orientation and margins existed. Everything that actually measures a document reads
// pageGeometry(pageSetup(editor)) instead -- see PageElements.
export const PAGE_HEIGHT = 1056;             // 11in
export const PAGE_MARGIN = 96;               // 1in, top and bottom
export { PAGE_GAP } from "./PageElements";
export const SHEET_PITCH = PAGE_HEIGHT + 24;
export const TEXT_HEIGHT = PAGE_HEIGHT - 2 * PAGE_MARGIN;        // 9in of writing

export const paginationKey = new PluginKey("parkourPagination");

type Pushes = Map<number, number>;           // document position -> pixels to push down
type Reserves = Map<number, number>;        // sheet index -> pixels kept back for its footnotes

const sameMap = (a: Pushes, b: Pushes) => a.size === b.size
    && Array.from(a).every(([pos, px]) => b.get(pos) === px);

/** The class on a footnote band, which is also how the block walk knows to skip it. */
export const BAND = "parkour-footnote-band";

/** The air between the last line of text and the rule above the notes. */
const FOOTNOTE_GAP = 14;

const sameBands = (a: Band[], b: Band[]) => a.length === b.length && a.every((band, index) =>
    band.sheet === b[index].sheet && band.notes.length === b[index].notes.length
    && band.notes.every((note, at) => note.number === b[index].notes[at].number
                                   && note.order === b[index].notes[at].order
                                   && note.text === b[index].notes[at].text));

/**
 * The notes for one sheet, drawn as a widget at the foot of it.
 *
 * ABSOLUTELY POSITIONED, so it is not in the text's flow and cannot push anything: the room it
 * needs is made by pagination, which stops the text short on that sheet, and the band is then
 * painted into the gap. A band that took its space by BEING in the flow would land wherever the
 * flow put it, which on a short page is halfway up it.
 *
 * `contenteditable=false` and `data-band`: it renders inside the editable element, so without the
 * first a reader could type into a footnote band and with no second the block walk would count it
 * as one of the document's own blocks -- which is how the table gripper once silently turned
 * pagination off altogether.
 */
function bandElement(band: Band, top: number, pitch: number, left: number, width: number) {
    const element = document.createElement("div");
    element.className = BAND;
    element.setAttribute("contenteditable", "false");
    element.setAttribute("data-footnote-band", String(band.sheet));
    element.style.position = "absolute";
    element.style.left = `${left}px`;
    element.style.width = `${width}px`;
    // THE FOOT OF THE SHEET, not the foot of the text. The band hangs from the bottom margin
    // upwards, so a sheet with one note and a sheet with six both end their notes in the same
    // place -- which is what makes a run of pages look like a document rather than a draft.
    element.style.bottom = "";
    element.style.top = `${top}px`;
    band.notes.forEach((note) => {
        const line = document.createElement("div");
        line.className = "parkour-footnote-note";
        const marker = document.createElement("sup");
        marker.textContent = String(note.number);
        line.appendChild(marker);
        line.appendChild(document.createTextNode(` ${note.text || ""}`));
        if (!(note.text || "").trim()) line.classList.add("parkour-footnote-note-empty");
        element.appendChild(line);
    });
    void pitch;
    return element;
}

export const Pagination = Extension.create({
    name: "pagination",

    addProseMirrorPlugins() {
        return [new Plugin({
            key: paginationKey,
            state: {
                init: () => ({
                    pushes: new Map() as Pushes, deco: DecorationSet.empty,
                    reserves: new Map() as Reserves, bands: [] as Band[],
                    // WHICH SHEET EACH NOTE'S MARKER LANDED ON, keyed by the note's order. Read by
                    // the marker numbering when the document restarts its numbers on each page --
                    // which is the one numbering that cannot be worked out from the body, because
                    // it is a property of the LAYOUT. See numberedByPage in footnotes.ts.
                    pageOf: new Map<number, number>(),
                    heights: new Map() as Reserves, geometry: { pitch: SHEET_PITCH, left: 0, width: 0 },
                }),
                apply(tr, value, _old, next) {
                    const incoming = tr.getMeta(paginationKey) as {
                        pushes: Pushes; reserves: Reserves; bands: Band[]; heights: Reserves;
                        pageOf: Map<number, number>;
                        geometry: { pitch: number; left: number; width: number; writable: number };
                    } | undefined;
                    if (incoming) {
                        const decorations = Array.from(incoming.pushes).map(([pos, px]) => {
                            const node = next.doc.nodeAt(pos);
                            return node && Decoration.node(pos, pos + node.nodeSize, {
                                style: `padding-top:${px}px`, "data-page-push": "",
                            });
                        }).filter(Boolean) as Decoration[];
                        // THE BANDS ARE WIDGETS AT POSITION 0 and are placed by CSS, not by where
                        // they sit in the document: a widget is drawn where its position says, and
                        // the foot of sheet four is not a position in the text. Each carries its
                        // sheet as a key so ProseMirror reuses the element instead of rebuilding
                        // every band on every keystroke.
                        const { pitch, left, width, writable } = incoming.geometry;
                        incoming.bands.forEach((band) => {
                            const height = incoming.heights.get(band.sheet) ?? 0;
                            const top = band.sheet * pitch + writable - height;
                            decorations.push(Decoration.widget(
                                0, () => bandElement(band, top, pitch, left, width),
                                // THE KEY CARRIES WHAT THE BAND SAYS, not only where it sits. A
                                // widget whose key has not changed is REUSED -- ProseMirror does
                                // not call the builder again -- so a band keyed on its sheet and
                                // its geometry kept drawing one note after a second was added
                                // beside it: the band had not moved, so nothing redrew it.
                                { side: -1, key: `footnote-band-${band.sheet}-${top}-${height}-`
                                    + band.notes.map(n => `${n.number}:${n.text}`).join("|") }));
                        });
                        return {
                            pushes: incoming.pushes, reserves: incoming.reserves,
                            bands: incoming.bands, heights: incoming.heights,
                            pageOf: incoming.pageOf, geometry: incoming.geometry,
                            deco: DecorationSet.create(next.doc, decorations),
                        };
                    }
                    // MAPPED THROUGH THE CHANGE rather than dropped, so a break does not flicker
                    // off and back on with every character typed above it.
                    //
                    // AND THE RESERVES ARE DROPPED ON A REAL CHANGE. They only grow within one
                    // settling run, which is what stops a note and its own reserve chasing each
                    // other between two sheets; an edit is where that run ends and the next begins,
                    // or a deleted note would keep its band of blank paper for ever.
                    return tr.docChanged
                        ? { ...value, reserves: new Map() as Reserves,
                            deco: value.deco.map(tr.mapping, tr.doc) }
                        : value;
                },
            },
            props: {
                decorations: (state) => paginationKey.getState(state)?.deco ?? DecorationSet.empty,
            },
        })];
    },
});

/**
 * Work out the breaks and, if they have moved, tell the editor.
 *
 * Returns the number of sheets. Call it after a change and after anything that can alter the
 * laid-out height; it is cheap and does nothing when the answer has not changed.
 */
export function measurePagination(editor: any, enabled: boolean): number {
    // THE VIEW IS NOT ALWAYS THERE. TipTap THROWS on `editor.view` between the editor being created
    // and its view being mounted -- "The editor view is not available" -- and this runs on every
    // render, which includes renders in that window. An exception here takes the page down for
    // something that will be true a frame later.
    //
    // AND THE GUARD HAS TO COVER `view.dom`, NOT ONLY `editor.view`. TipTap v3 hands back a PROXY
    // that throws on property access -- the message says so: "Cannot access view['dom']" -- so
    // obtaining the view succeeds and reading from it is what throws. Catching only the first
    // access left an uncaught runtime error on every unmount, which in development is a red
    // overlay over the whole application and in production is an exception nobody sees.
    let view: any = null;
    let dom: HTMLElement | null = null;
    try {
        view = editor?.view ?? null;
        dom = (view?.dom as HTMLElement) ?? null;
    } catch {
        return 1;
    }
    if (!view || !dom) return 1;

    const page = dom.closest(".parkour-page") as HTMLElement | null;
    const state = paginationKey.getState(view.state);
    const current: Pushes = state?.pushes ?? new Map();
    const heldBack: Reserves = state?.reserves ?? new Map();

    // THE DOCUMENT'S OWN GEOMETRY, not this module's defaults. A landscape sheet breaks at 8.5in
    // and a document with half-inch margins fits an inch more writing on each page; measuring
    // either against letter-portrait-one-inch puts the preview's breaks somewhere the renderer
    // will not put them, which is the one thing a pagination PREVIEW must not do.
    const { sheetPitch: pitch, textHeight: writable, columns } = pageGeometry(pageSetup(editor));

    // A MULTI-COLUMN DOCUMENT IS NOT PREVIEWED, and the reason is mechanical rather than a
    // preference. This preview simulates a page break by pushing a block DOWN with a padding
    // decoration; the next column is SIDEWAYS, and padding cannot put a block there. Drawing sheet
    // boundaries anyway would show breaks in places the renderer will not break -- which is the one
    // thing a pagination preview must never do, and worse than showing none.
    //
    // THE BROWSER STILL FLOWS THE COLUMNS (DocumentPaper sets column-count), so the text on screen
    // reads in the right order and at the right measure; what is missing is only where the pages
    // end. The Page setup dialog says so at the moment the choice is made, and the PDF lays the
    // pages out properly either way.
    if (!enabled || columns > 1) {
        // AND THE BANDS GO WITH IT. Off, there are no sheets, so there is no foot of a page for a
        // note to sit at; they are shown in the pane and in the PDF instead.
        if (current.size || (state?.bands?.length ?? 0)) view.dispatch(view.state.tr.setMeta(
            paginationKey, { pushes: new Map(), reserves: new Map(), bands: [], heights: new Map(),
                             // AND THE PAGE MAP, because with the preview off there are no pages:
                             // per-page numbering has nothing to restart on and falls back to
                             // numbering straight through, which is what an empty map means.
                             pageOf: new Map(),
                             geometry: { pitch, left: 0, width: 0, writable } }));
        page?.style.removeProperty("min-height");
        return 1;
    }

    // THE TOP-LEVEL BLOCKS, paired with their document positions. ProseMirror renders one element
    // per top-level node in order, so the two walk together -- and the position, not the index, is
    // what a decoration is attached to, because an edit moves every position after it.
    // THE BLOCKS, AND ONLY THE BLOCKS. A widget decoration renders as a real element among the
    // editor's children -- the table gripper is one -- so `dom.children` stopped being one-to-one
    // with the document's top-level nodes the day one was added, the count check below failed every
    // time, and pagination silently did nothing: a ten-page procedure reported one sheet and drew
    // no breaks. Widgets are chrome; they are skipped by the class they are drawn with.
    const children = (Array.from(dom.children) as HTMLElement[])
        .filter((element) => !element.classList.contains("parkour-table-gripper-anchor")
                          && !element.classList.contains(BAND));
    const { doc } = view.state;
    if (children.length !== doc.childCount) return 1;        // mid-render; the next pass will do it

    let position = 0;
    let shift = 0;          // the pushes this pass is adding, above the current block
    let appliedAbove = 0;   // the pushes ALREADY in the layout, above the current block
    let bottom = 0;
    const next: Pushes = new Map();

    for (let index = 0; index < children.length; index += 1) {
        const element = children[index];
        const node = doc.child(index);
        const already = current.get(position) ?? 0;

        // NATURAL GEOMETRY: where this block would sit, and how tall it would be, with no pushes
        // at all. offsetTop already contains every push above it, and its own push is padding
        // inside its own box, so both come back out here.
        const naturalTop = element.offsetTop - appliedAbove;
        const height = element.offsetHeight - already;

        const top = naturalTop + shift;

        // AN EXPLICIT BREAK IS THE END OF THE SHEET, whatever is left on it. The same mechanism as
        // a block that does not fit -- the break itself is pushed to the next sheet's top, and
        // everything after it follows naturally -- so there is one way a page ends and not two.
        //
        // NOT WHEN IT IS ALREADY AT THE TOP. A break landing exactly on a sheet boundary would
        // otherwise push a whole empty page, which is what somebody gets when they add a break and
        // then delete the paragraph above it.
        if (node.type?.name === "pageBreak") {
            const sheet = Math.floor(top / pitch);
            const sheetTop = sheet * pitch;
            if (top > sheetTop + 1) {
                const push = (sheet + 1) * pitch - top;
                next.set(position, Math.round(push));
                shift += push;
            }
        } else if (height > 0 && height <= writable) {
            const sheet = Math.floor(top / pitch);
            // THE ROOM ON THIS SHEET, WHICH IS NOT THE ROOM ON EVERY SHEET. A sheet carrying
            // footnotes has less of it, by exactly the height of its band -- that is how a note
            // gets to the foot of its own page instead of being drawn over the last line of text.
            const room = writable - (heldBack.get(sheet) ?? 0);
            if (top + height > sheet * pitch + room) {
                const push = (sheet + 1) * pitch - top;
                next.set(position, Math.round(push));
                shift += push;
            }
        }
        bottom = Math.max(bottom, top + (next.get(position) ?? 0) + height);
        appliedAbove += already;
        position += node.nodeSize;
    }

    // THE FOOTNOTES, PLACED BY WHERE THEIR MARKERS ENDED UP. Measured after the pushes above have
    // been applied, so a marker that moved to the next sheet takes its note with it.
    //
    // MEASURED FROM THE MARKER'S OWN ELEMENT, not from coordsAtPos. The editor is inside a page
    // that scrolls and is transformed at some zoom levels; offsetTop against the positioned
    // .ProseMirror box is the same coordinate space the block walk above already works in, and
    // mixing the two is how a note ends up one sheet out.
    // NUMBERED THE WAY THE MARKERS ARE NUMBERED, restart and all -- the band at the foot of the
    // sheet has to say what the marker in the text says. The rule lives in footnotes.ts; this walk
    // applies it to the LIVE document rather than to a serialised copy, because it also needs each
    // marker's DOM node, which only the view has.
    const how = doc.attrs?.footnoteNumbering;
    const restart = how === "section";
    const markers: { number: number; text: string; top: number; order: number }[] = [];
    let counted = 0;
    let within = 0;
    doc.descendants((child: any, at: number, parent: any) => {
        if (restart && parent === doc && child.type.name === "heading"
            && Number(child.attrs?.level) === 1) within = 0;
        if (child.type.name !== "footnote") return;
        counted += 1;
        within += 1;
        let element: any = null;
        try { element = view.nodeDOM(at); } catch { element = null; }
        const measured = element && typeof element.offsetTop === "number" ? element.offsetTop : null;
        if (measured === null) return;
        // THE SAME CORRECTION THE BLOCK WALK MAKES, for the same reason: offsetTop carries the
        // pushes ALREADY in the layout, and what this needs is where the marker will be once the
        // pushes computed this pass are applied. Taking the old ones out and the new ones in is
        // `naturalTop + shift` by another name -- adding only the new ones counts the unchanged
        // pushes twice, which on a settled document moves every note a sheet further down each
        // time it is measured.
        markers.push({ number: restart ? within : counted, text: child.attrs?.text || "",
                       order: counted - 1,
                       top: measured - above(current, at) + above(next, at) });
    });
    // PER PAGE, THE NUMBER IS THE POSITION ON THE SHEET, and this is the only place that knows it:
    // the marker decoration reads the sheet map this pass publishes, so the band and the marker in
    // the text are computed from the same measurement rather than from two walks that might
    // disagree about which page a note is on.
    if (how === "page") {
        const seen = new Map<number, number>();
        markers.slice().sort((a, b) => a.order - b.order).forEach((marker) => {
            const sheet = Math.max(0, Math.floor(marker.top / pitch));
            const at = (seen.get(sheet) ?? 0) + 1;
            seen.set(sheet, at);
            marker.number = at;
        });
    }

    const bands = bandsFor(markers, pitch);

    // THE BAND'S HEIGHT IS THE BAND'S TO SAY. Predicting it from the number of notes means owning a
    // second copy of the stylesheet's line height; reading it back off the element that is already
    // on the page cannot drift.
    const heights: Reserves = new Map();
    bands.forEach((band) => {
        const element = dom.querySelector(`[data-footnote-band="${band.sheet}"]`) as HTMLElement | null;
        heights.set(band.sheet, element ? Math.ceil(element.offsetHeight) + FOOTNOTE_GAP : 0);
    });
    const reserves = grown(heldBack, heights);

    const pageOf = new Map<number, number>();
    markers.forEach((marker) => {
        pageOf.set(marker.order, Math.max(0, Math.floor(marker.top / pitch)));
    });

    const geometry = { pitch, left: 0, width: dom.clientWidth, writable };
    const moved = !sameMap(next, current) || !sameMap(reserves, heldBack)
        || !sameBands(bands, state?.bands ?? []) || !sameMap(heights, state?.heights ?? new Map())
        || !sameMap(pageOf, state?.pageOf ?? new Map());
    if (moved) view.dispatch(view.state.tr.setMeta(paginationKey,
        { pushes: next, reserves, bands, heights, pageOf, geometry }));

    const sheets = Math.max(1, Math.ceil((bottom + 1) / pitch));
    if (page) page.style.minHeight = `${sheets * pitch - GAP}px`;
    return sheets;
}

/**
 * The pushes in one map that lie above a position.
 *
 * A MARKER'S MEASURED TOP IS YESTERDAY'S. The pushes computed this pass are not in the layout until
 * the transaction is applied and the browser has drawn it, so a marker below a block that is about
 * to move measures one sheet too high -- and its note would be drawn at the foot of a page its
 * reference is no longer on, which is the single thing a footnote must never do.
 */
function above(pushes: Pushes, position: number): number {
    let total = 0;
    pushes.forEach((px, at) => { if (at < position) total += px; });
    return total;
}
